<template>
  <el-container class="login-container">
    <el-main class="login-main">
      <div class="login-wrapper">
        <el-card class="login-card" shadow="hover">
          <template #header>
            <div class="login-header">
              <el-icon class="header-icon">
                <User/>
              </el-icon>
              <h1 class="login-title">登录</h1>
            </div>
          </template>

          <el-form
              ref="loginFormRef"
              :model="loginForm"
              :rules="rules"
              label-position="top"
              class="login-form"
          >
            <el-form-item label="用户名" prop="username">
              <el-input
                  v-model="loginForm.username"
                  placeholder="请输入用户名"
                  prefix-icon="User"
                  clearable
                  size="large"
              />
            </el-form-item>

            <el-form-item label="密码" prop="password">
              <el-input
                  v-model="loginForm.password"
                  type="password"
                  placeholder="请输入密码"
                  prefix-icon="Lock"
                  show-password
                  clearable
                  size="large"
              />
            </el-form-item>

            <el-form-item>
              <div class="form-options">
                <el-checkbox v-model="loginForm.rememberMe">记住我</el-checkbox>
              </div>
            </el-form-item>

            <el-form-item>
              <el-button
                  type="primary"
                  :loading="loading"
                  @click="handleLogin"
                  style="width: 100%"
                  size="large"
              >
                登录
              </el-button>
            </el-form-item>

            <div class="register-hint">
              <span class="hint-text">还没有账号？</span>
              <el-link type="primary" @click="showRegisterDialog = true">
                注册新账号
              </el-link>
            </div>
          </el-form>
        </el-card>

        <!-- 注册对话框 -->
        <RegisterDialog
          v-model="showRegisterDialog"
          @registered="handleRegistered"
        />
      </div>
    </el-main>
    <div class="beian-container">
      <a href="https://beian.miit.gov.cn" target="_blank" class="beian-link">
        {{ beianNumber }}
      </a>
    </div>
   
  </el-container>
   
</template>

<script setup>
import {ref, reactive, onMounted, onUnmounted} from 'vue'
import {ElMessage} from 'element-plus'
import {useRouter} from 'vue-router'
import {User, Lock} from '@element-plus/icons-vue'
import axios from "axios";
import {useWuliu178AuthStore} from "@store/auth.js";
import {postWlApi, getWlApi} from '@utils/api_v1.js'
import {getBaseURL} from '@utils/api_v1.js'
import RegisterDialog from '@components/RegisterDialog.vue'

const router = useRouter()
const loginFormRef = ref()
const loading = ref(false)
const beianNumber = ref('')
// 微信绑定令牌：从URL获取，登录成功后用于绑定openid
const bindToken = ref('')
// 注册对话框
const showRegisterDialog = ref(false)

const loginForm = reactive({
  username: '',
  password: '',
  rememberMe: false
})

const rules = {
  username: [
    {required: true, message: '请输入用户名', trigger: 'blur'},
    // { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符之间', trigger: 'blur' }
  ],
  password: [
    {required: true, message: '请输入密码', trigger: 'blur'},
    // { min: 6, max: 20, message: '密码长度在 6 到 20 个字符之间', trigger: 'blur' }
  ]
}

// 域名与备案号的映射关系
const domainBeianMap = {
  'wuliu178.com': '浙ICP备2025220853号-1',
  'wuliu369.com': '沪ICP备2026006474号-1'
  // 可以添加更多域名和对应的备案号
}

// 根据域名获取备案号
const getBeianByDomain = () => {
  const currentDomain = window.location.hostname;
  console.log('当前域名:', currentDomain);
  
  // 遍历映射关系，查找匹配的域名
  for (const domain in domainBeianMap) {
    if (currentDomain.includes(domain)) {
      return domainBeianMap[domain];
    }
  }
  
  // 默认备案号
  return '';
}

// 微信OAuth跳转过来，URL中带code参数：调后端接口换openid
const handleWechatCode = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');

  if (!code) return false;

  // 清除URL中的code参数，避免刷新重复触发
  router.replace('/login');

  try {
    const res = await getWlApi('/wechat/auth/callback', { code });
    if (res.code !== 0) {
      ElMessage.error(res.message || '微信授权失败');
      return true;
    }

    const data = res.data;
    if (data.is_bound) {
      // 已绑定：直接登录
      const wl_auth = useWuliu178AuthStore();
      wl_auth.login({
        access_token: data.access,
        refresh_token: data.refresh,
        rememberMe: false,
      });
      fetchUserInfoAfterLogin().then(() => {
        router.replace('/home');
      });
    } else {
      // 未绑定：保存 bind_token，等用户登录后绑定
      bindToken.value = data.bind_token;
      ElMessage.info('请登录以完成微信账号绑定');
    }
  } catch (err) {
    console.error('微信授权失败', err);
    ElMessage.error('微信授权失败，请重试');
  }
  return true;
};

// 使用 bind_token 绑定微信账号，成功后退出并返回微信
const bindWechatAccount = async (bindToken) => {
  const wl_auth = useWuliu178AuthStore();
  try {
    const res = await postWlApi('/wechat/auth/bind', { bind_token: bindToken });
    if (res.code === 0) {
      ElMessage.success('微信账号绑定成功，正在返回微信...');

      // 退出登录（黑名单token + 清除本地状态）
      try {
        await postWlApi('/token/logout', { token: wl_auth.refresh_token });
      } catch (e) {
        // logout 失败不阻塞流程
      }
      wl_auth.logout();

      // 关闭微信网页窗口，返回微信
      const closeWeixinWindow = () => {
        if (typeof WeixinJSBridge !== 'undefined') {
          WeixinJSBridge.call('closeWindow');
        } else {
          document.addEventListener('WeixinJSBridgeReady', () => {
            WeixinJSBridge.call('closeWindow');
          }, false);
        }
      };

      setTimeout(() => {
        closeWeixinWindow();
        // 非微信环境（如浏览器测试）2秒后仍未关闭则跳回登录页
        setTimeout(() => {
          if (typeof WeixinJSBridge === 'undefined') {
            router.replace('/login');
          }
        }, 2000);
      }, 1500);

      return true;
    } else {
      ElMessage.warning(res.message || '微信账号绑定失败');
      return false;
    }
  } catch (err) {
    console.error('微信绑定失败', err);
    ElMessage.warning('微信账号绑定失败，可在设置中重新绑定');
    return false;
  }
};

onMounted(async () => {
  const wl_auth = useWuliu178AuthStore();

  if (wl_auth.rememberMe) {
    loginForm.rememberMe = true;
  }

  // 处理微信OAuth跳转的code参数
  const hasWechatCode = await handleWechatCode();

  // 如果没有微信code，走原有的记住我自动登录逻辑
  if (!hasWechatCode) {
    if (wl_auth.rememberMe && wl_auth.access_token) {
      postWlApi(`/user/profile/`).then((res) => {
        if (res.code === 0) {
          wl_auth.setUserProfile(res.data);
          router.push('/home');
        }
      }).catch((err) => {
        console.error('自动登录失败', err);
      });
    }
  }

  // 根据当前域名设置备案号
  beianNumber.value = getBeianByDomain();

  // // 添加全局Enter键监听
  // const handleKeydown = (event) => {
  //   if (event.key === 'Enter') {
  //     handleLogin();
  //   }
  // };
  //
  // window.addEventListener('keydown', handleKeydown);
  //
  // // 组件卸载时移除监听
  // onUnmounted(() => {
  //   window.removeEventListener('keydown', handleKeydown);
  // });
})

// 登录成功后获取用户信息和菜单权限
const fetchUserInfoAfterLogin = async () => {
  try {
    const res = await postWlApi('/user/profile');
    if (res && res.data) {
      const wl_auth = useWuliu178AuthStore();
      wl_auth.setUserProfile(res.data);
    }
  } catch (error) {
    console.error('获取用户信息失败', error);
  }
};

const handleLogin = () => {
  const wl_auth = useWuliu178AuthStore()
  loginFormRef.value.validate((valid) => {
    if (valid) {
      loading.value = true
      const login_promise = postWlApi('/token/login', {
        username: loginForm.username,
        password: loginForm.password
      })

      login_promise
          .then(response => {
            // console.log('登录成功:', response)
            // 处理成功逻辑
            if (response.code === 0) {
              wl_auth.login({
                access_token: response.data.access,
                refresh_token: response.data.refresh,
                rememberMe: loginForm.rememberMe
              });

              // 如果有微信绑定令牌，先绑定，成功后退出返回微信
              if (bindToken.value) {
                const tokenToBind = bindToken.value;
                bindToken.value = '';  // 清除，避免重复绑定
                bindWechatAccount(tokenToBind).then((bindSuccess) => {
                  if (!bindSuccess) {
                    // 绑定失败，退出登录，留在登录页让用户重试
                    try {
                      postWlApi('/token/logout', { token: wl_auth.refresh_token });
                    } catch (e) { /* ignore */ }
                    wl_auth.logout();
                  }
                  // 绑定成功时 bindWechatAccount 内部已处理退出和关闭窗口
                });
              } else {
                // 登录成功后获取用户信息和菜单权限
                fetchUserInfoAfterLogin().then(() => {
                  router.push('/home');
                });
              }
            } else {
              ElMessage.error('登录信息有误，请检查用户名和密码')
            }
          })
          .catch(error => {

            // 添加登录失败提示
            ElMessage.error('登录信息有误，请检查用户名和密码')

          })
          .finally(() => {
            // 无论成功或失败，都要重置加载状态
            loading.value = false
          })

    } else {
      return false
    }
  })
}

// 注册成功后的回调：自动填充手机号到登录表单
const handleRegistered = (data) => {
  if (data && data.username) {
    loginForm.username = data.username
  }
}
</script>

<style scoped>
.login-container {
  height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  overflow: hidden;
}

.login-main {
  height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  background: transparent;
}

.login-wrapper {
  width: 100%;
  max-width: 420px;
  min-width: 420px;
  padding: 20px;
}

.login-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.login-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.header-icon {
  font-size: 28px;
  color: #409eff;
  margin-right: 12px;
}

.login-title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.login-form {
  padding: 0 20px 20px;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.forgot-password {
  font-size: 14px;
}

.beian-container {
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.beian-link {
  font-size: 12px;
  color: #909399;
  text-decoration: none;
}

.beian-link:hover {
  color: #409eff;
}

.register-hint {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: -8px;
  margin-bottom: 12px;
  padding: 0 2px;
}

.register-hint .hint-text {
  font-size: 13px;
  color: #909399;
  margin-right: 4px;
}
</style>

<style>
/* 全局样式重置，确保页面充满整个屏幕 */
html, body, #app {
  height: 100%;
  width: 100%;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

/* 修复Element Plus容器样式 */
.el-container {
  height: 100%;
  display: block;
}

.el-main {
  padding: 0;
}
</style>
